import {
  collection,
  doc,
  getDocs,
  setDoc,
  deleteDoc,
  query,
  orderBy
} from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  calculateCartWeightKg,
  detectShippingZone,
  calculateShippingCostByWeight,
  buildWeightBracketsForTariff,
  ZONE_TO_TARIFF,
  SHIPPING_ZONE_LABELS
} from '../utils/shippingUtils';

const COLLECTION_NAME = 'shipping_rates';
const CODE_BASED_ZONES = Object.keys(ZONE_TO_TARIFF);

function sortByMax(a, b, key) {
  const maxA = a[key] == null || a[key] === '' ? Infinity : Number(a[key]);
  const maxB = b[key] == null || b[key] === '' ? Infinity : Number(b[key]);
  return maxA - maxB;
}

function pickWeightBracket(brackets, weightKg) {
  if (!brackets?.length) return null;
  const sorted = [...brackets].sort((a, b) => sortByMax(a, b, 'maxKg'));
  for (const bracket of sorted) {
    const maxKg = bracket.maxKg == null || bracket.maxKg === '' ? Infinity : Number(bracket.maxKg);
    if (weightKg <= maxKg) return bracket;
  }
  return sorted[sorted.length - 1];
}

function pickOrderValueBracket(brackets, orderValue) {
  if (!brackets?.length) return null;
  const sorted = [...brackets].sort((a, b) => sortByMax(a, b, 'maxAmount'));
  for (const bracket of sorted) {
    const maxAmount =
      bracket.maxAmount == null || bracket.maxAmount === '' ? Infinity : Number(bracket.maxAmount);
    if (orderValue <= maxAmount) return bracket;
  }
  return sorted[sorted.length - 1];
}

/**
 * Cálculo principal: tarifas por CP + peso (código fuente de verdad).
 */
export function computeShippingFromRate(rate, weightKg, orderValue = 0, zone = null) {
  const resolvedZone = zone || rate?.zone;

  if (resolvedZone && CODE_BASED_ZONES.includes(resolvedZone)) {
    const result = calculateShippingCostByWeight(resolvedZone, weightKg);
    return {
      cost: result.cost,
      freeShipping: false,
      rateName: result.rateName,
      zone: resolvedZone,
      bracketLabel: result.bracketLabel,
      pricingMethod: result.pricingMethod,
      weightKg: result.weightKg,
      originalCost: result.cost,
      freeShippingThreshold: null,
      deliveryDays: rate?.deliveryDays || defaultDeliveryDays(resolvedZone),
      extraKg: result.extraKg,
      extraPerKg: result.extraPerKg,
      tariffKey: result.tariffKey
    };
  }

  if (!rate) {
    return {
      cost: 0,
      freeShipping: false,
      rateName: 'No disponible',
      bracketLabel: null,
      pricingMethod: 'none',
      weightKg
    };
  }

  if (rate.alwaysFree) {
    return {
      cost: 0,
      freeShipping: true,
      rateName: rate.name,
      zone: rate.zone,
      bracketLabel: 'Envío gratuito',
      pricingMethod: 'always_free',
      weightKg,
      originalCost: 0,
      freeShippingThreshold: null,
      deliveryDays: rate.deliveryDays || null
    };
  }

  let cost = 0;
  let bracketLabel = null;
  let pricingMethod = 'legacy';

  const weightBracket = pickWeightBracket(rate.weightBrackets, weightKg);
  const valueBracket = pickOrderValueBracket(rate.orderValueBrackets, orderValue);

  if (weightBracket) {
    if (weightKg > 10 && weightBracket.extraPerKg) {
      const extraKg = Math.ceil(weightKg - 10);
      cost = Number(weightBracket.cost) + extraKg * Number(weightBracket.extraPerKg);
      bracketLabel = `Hasta 10 kg + ${extraKg} kg adicional`;
      pricingMethod = 'weight_with_supplement';
    } else {
      cost = Number(weightBracket.cost) || 0;
      bracketLabel = weightBracket.label || `Hasta ${weightBracket.maxKg} kg`;
      pricingMethod = 'weight_bracket';
    }
  } else if (valueBracket) {
    cost = Number(valueBracket.cost) || 0;
    bracketLabel = valueBracket.label || `Hasta ${valueBracket.maxAmount} €`;
    pricingMethod = 'order_value_bracket';
  } else {
    cost = Number(rate.baseCost) || 0;
    cost += weightKg * (Number(rate.weightMultiplier) || 0);
    pricingMethod = 'base_plus_weight';
    bracketLabel = `Base €${Number(rate.baseCost || 0).toFixed(2)} + peso`;
  }

  const originalCost = Math.max(cost, 0);

  if (rate.freeShippingThreshold && orderValue >= Number(rate.freeShippingThreshold)) {
    return {
      cost: 0,
      freeShipping: true,
      rateName: rate.name,
      zone: rate.zone,
      bracketLabel,
      pricingMethod,
      weightKg,
      originalCost,
      freeShippingThreshold: rate.freeShippingThreshold,
      deliveryDays: rate.deliveryDays || null
    };
  }

  return {
    cost: Math.max(cost, 0),
    freeShipping: false,
    rateName: rate.name,
    zone: rate.zone,
    bracketLabel,
    pricingMethod,
    weightKg,
    originalCost,
    freeShippingThreshold: rate.freeShippingThreshold || null,
    deliveryDays: rate.deliveryDays || null
  };
}

function defaultDeliveryDays(zone) {
  if (zone === 'huelva') return '1-2 días laborables';
  if (zone === 'andalucia' || zone === 'peninsula' || zone === 'balearic') {
    return '2-3 días laborables';
  }
  return '5-7 días laborables';
}

function buildDefaultRate(zone) {
  const tariffKey = ZONE_TO_TARIFF[zone];
  return {
    id: `shipping_${zone}`,
    name: SHIPPING_ZONE_LABELS[zone] || zone,
    zone,
    alwaysFree: false,
    baseCost: 0,
    weightMultiplier: 0,
    freeShippingThreshold: null,
    deliveryDays: defaultDeliveryDays(zone),
    active: true,
    weightBrackets: buildWeightBracketsForTariff(tariffKey),
    orderValueBrackets: []
  };
}

export const defaultShippingRates = CODE_BASED_ZONES.map((zone) => buildDefaultRate(zone));

export const firebaseShippingService = {
  async getAll() {
    try {
      const q = query(collection(db, COLLECTION_NAME), orderBy('name'));
      const querySnapshot = await getDocs(q);
      const rates = [];

      querySnapshot.forEach((docSnap) => {
        rates.push({
          id: docSnap.id,
          ...docSnap.data()
        });
      });

      return rates;
    } catch (error) {
      console.error('Error al obtener tarifas de envío:', error);
      throw error;
    }
  },

  async createOrUpdate(rateData) {
    try {
      const { id, ...data } = rateData;
      const docId = id || `shipping_${Date.now()}`;

      const rateToSave = {
        ...data,
        weightBrackets: data.weightBrackets || [],
        orderValueBrackets: data.orderValueBrackets || [],
        updatedAt: new Date(),
        createdAt: data.createdAt || new Date()
      };

      await setDoc(doc(db, COLLECTION_NAME, docId), rateToSave);
      return { id: docId, ...rateToSave };
    } catch (error) {
      console.error('Error al guardar tarifa de envío:', error);
      throw error;
    }
  },

  async delete(id) {
    try {
      await deleteDoc(doc(db, COLLECTION_NAME, id));
      return true;
    } catch (error) {
      console.error('Error al eliminar tarifa de envío:', error);
      throw error;
    }
  },

  async getByZone(zone) {
    const rates = await this.getAll();
    return rates.find((rate) => rate.zone === zone && rate.active !== false);
  },

  async getActiveRates() {
    const rates = await this.getAll();
    return rates.filter((rate) => rate.active !== false);
  },

  detectZone(postalCode, province) {
    return detectShippingZone(postalCode, province);
  },

  async calculateShipping(zone, weight = 1, orderValue = 0) {
    try {
      const rate =
        defaultShippingRates.find((item) => item.zone === zone) ||
        (await this.getByZone(zone));
      return computeShippingFromRate(rate, weight, orderValue, zone);
    } catch (error) {
      console.error('Error al calcular envío:', error);
      return { cost: 0, freeShipping: false, rateName: 'Error', weightKg: weight };
    }
  },

  async calculateForCart(cart, options = {}) {
    const {
      postalCode = '',
      province = '',
      zone: zoneOverride = null,
      orderValue: orderValueOverride = null
    } = options;

    const weightKg = calculateCartWeightKg(cart);
    const orderValue =
      orderValueOverride != null
        ? orderValueOverride
        : cart.reduce((t, item) => t + item.price * item.quantity, 0);

    const zone = zoneOverride || detectShippingZone(postalCode, province);
    const result = await this.calculateShipping(zone, weightKg, orderValue);

    return {
      ...result,
      zone,
      bottles: cart.reduce((n, item) => n + item.quantity, 0)
    };
  }
};

export const initializeShippingRates = async () => {
  try {
    for (const rate of defaultShippingRates) {
      await firebaseShippingService.createOrUpdate(rate);
    }
  } catch (error) {
    console.error('Error al inicializar tarifas de envío:', error);
  }
};
